import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {QuoteService} from '../../shared/quote.service';
import {QuoteRow} from '../../interfaces/quote';
import {SubscribedComponent} from '../../subscribed.component';
import {DualAxisDelegate, DualAxisEvent} from '../../dual-axis/dual-axis.component';
import {ColDef, ColType} from '../../interfaces/colDef';
import {TreeNode} from '../../interfaces/treenode';
import {TableOptions} from '../../settings/table-options/table-options.component';
import {Paged} from '../../interfaces/paged';

// Open (Draft and Submitted) first: the quotes still waiting on someone. Superseded has no tab; All shows it.
const TABS = ['open', 'all', 'draft', 'submitted', 'accepted', 'rejected', 'expired'];

// Organisation and title are the open-ended text; they share what the other columns leave (see invoices).
const NAME_COL = {maxWidth: '0', minWidth: '120px', width: '50%', textOverflow: 'ellipsis'};

/** Every quote of the tenant on the standard list shell (#1698). The server filters by tab and sends the newest first;
 *  sorting and paging run on the tab's list. */
@Component({
  selector: 'bst-quotes-overview',
  templateUrl: './quotes-overview.component.html',
  styleUrls: ['./quotes-overview.component.scss'],
  standalone: false
})
export class QuotesOverviewComponent extends SubscribedComponent implements OnInit, DualAxisDelegate<QuoteRow> {
  @ViewChild('cellTemplate', {static: true, read: TemplateRef}) cellTemplateOutlet: TemplateRef<any>;
  @ViewChild('cardTemplate', {static: true, read: TemplateRef}) cardTemplateOutlet: TemplateRef<any>;

  tabs: {title: string, route: string, responsive: boolean}[] = [];
  status = 'open';
  count: number = null;
  events = new Subject<DualAxisEvent>();
  data: TreeNode<QuoteRow>[] = [];
  stickyColumnsLeft: ColDef[] = [
    {name: 'number', title: 'quotes.field.number', type: ColType.STRING, required: true, filter: false, sort: true},
  ];
  standardColumns: ColDef[] = [
    {name: 'version', title: 'quotes.field.version', type: ColType.INT, required: false, filter: false, sort: true},
    {name: 'status', title: 'quotes.field.status', type: ColType.STRING, required: false, filter: false, sort: true},
    {name: 'organisation', title: 'quotes.field.organisation', type: ColType.STRING, required: false, filter: false, sort: true, style: NAME_COL},
    {name: 'title', title: 'quotes.field.title', type: ColType.STRING, required: false, filter: false, sort: true, style: NAME_COL},
    {name: 'responsible', title: 'quotes.field.responsible', type: ColType.STRING, required: false, filter: false, sort: true},
    {name: 'quote_date', title: 'quotes.field.quote_date', type: ColType.DATE, required: false, filter: false, sort: true},
    {name: 'expires_on', title: 'quotes.field.expires_on', type: ColType.DATE, required: false, filter: false, sort: true},
  ];
  stickyColumnsRight: ColDef[] = [];
  options: TableOptions = {
    display: [...this.stickyColumnsLeft, ...this.standardColumns].map(c => ({col: c.name, shown: true})),
    others: {}, filter: {}, sorts: [],
  };

  constructor(private quotes: QuoteService, private router: Router, private translate: TranslateService, private route: ActivatedRoute) {
    super();
  }

  get headerTitle() {
    return {title: 'quotes.overview', translate: true};
  }

  get lang(): string {
    return this.translate.currentLang ?? this.translate.defaultLang;
  }

  ngOnInit() {
    super.ngOnInit();
    const keys = TABS.map(t => 'quotes.tab.' + t);
    this.subs.push(this.translate.stream(keys).subscribe(titles =>
      this.tabs = TABS.map((t, i) => ({title: titles[keys[i]], route: `/quotes/view/${t}`, responsive: true}))));
    this.subs.push(this.route.paramMap.subscribe(params => {
      const status = params.get('status');
      if (!TABS.includes(status)) {
        void this.router.navigate(['/quotes/view/open'], {replaceUrl: true});
        return;
      }
      this.status = status;
      this.events.next(DualAxisEvent.RESET);
    }));
  }

  private sortValue(q: QuoteRow, col: string): string | number {
    switch (col) {
      case 'organisation': return q.organisation.name ?? '';
      case 'responsible': return q.responsible?.name ?? '';
      case 'status': return this.translate.instant('quotes.status.' + q.status);
      case 'version': return q.version;
      default: return q[col] ?? '';
    }
  }

  async paged(page: number, pageSize: number): Promise<Paged<QuoteRow>> {
    this.loading.next(true);
    try {
      const status = this.status;
      const rows = await this.quotes.all(status as any);
      const sort = this.options.sorts?.[0];
      if (sort) {
        const dir = sort.dir === 2 ? -1 : 1;
        rows.sort((a, b) => {
          const x = this.sortValue(a, sort.col), y = this.sortValue(b, sort.col);
          return dir * (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), this.lang));
        });
      }
      const from = (page - 1) * pageSize;
      const slice = rows.slice(from, from + pageSize);
      // A slower answer for a tab already left must not overwrite the current one.
      if (status === this.status) {
        this.count = rows.length;
        this.data = slice.map(q => ({data: q, expanded: false, children: []}));
      }
      return {current_page: page, data: slice, from: slice.length ? from + 1 : 0, to: from + slice.length,
        last_page: Math.max(1, Math.ceil(rows.length / pageSize)), per_page: pageSize, total: rows.length};
    } finally {
      this.loading.next(false);
    }
  }

  selectRow(_event: MouseEvent, node: TreeNode<QuoteRow>) {
    this.router.navigate(['/quotes', node.data.id]);
  }
}
